import type { TransitionV2DataQuery } from '@revelio/data-access';

import type {
  PrimaryEntity,
  TransitionChartType,
} from '../../PageConfigProvider/types/chart';
import type { TransitionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { TransitionMetricValues } from '../../PageConfigProvider/types/metric';
import type { TransitionData } from '../TransitionDataProvider';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type TransitionBreakdownData,
} from '../types';

type TransitionsEntity = NonNullable<
  TransitionV2DataQuery['transitionsV2']
>['entities'][number];

/**
 * Pick the response entity whose metadata id matches `selectedEntity`. Falls
 * back to the first entity when there's no selection, no match, or the
 * selection id can't be parsed.
 */
const pickSelectedEntity = (
  entities: TransitionsEntity[] | undefined,
  selectedEntity: PrimaryEntity | undefined
): TransitionsEntity | undefined => {
  if (!entities || entities.length === 0) return undefined;
  if (!selectedEntity) return entities[0];

  const lastToken = selectedEntity.id.split('_').pop();
  const numericId = Number(lastToken);
  if (!Number.isFinite(numericId)) return entities[0];

  return entities.find((e) => e.metadata?.id === numericId) ?? entities[0];
};

export const convertTransitionData = ({
  metric,
  secondaryDimension,
  data,
  chartType,
  selectedEntity,
  limit,
}: {
  metric: TransitionMetricValues;
  secondaryDimension?: TransitionSecondaryDimensionValues;
  chartType: TransitionChartType;
  data: NonNullable<TransitionV2DataQuery['transitionsV2']>;
  selectedEntity?: PrimaryEntity;
  limit?: number;
}): TransitionData => {
  if (metric === 'transitions_inflows_and_outflows') {
    if (chartType === 'sankey' || chartType === 'chord') {
      return convertInflowsAndOutflowsSankeyData({ data });
    }
    return convertInflowsAndOutflowsData({
      data,
      secondaryDimension,
      selectedEntity,
      limit,
    });
  }

  if (secondaryDimension) {
    const isInflow = metric === 'transitions_inflow';

    // For table views, include ALL selected entities and carry the primary entity label
    if (chartType === 'table') {
      const buildItems = (
        kind: 'current' | 'flow'
      ): Array<SingleDataPoint<{ primary: string }>> =>
        data.entities.flatMap((entity) => {
          const primaryLabel = entity.metadata?.short_name;
          const breakdowns =
            entity.metrics?.breakdowns?.[secondaryDimension] ?? [];
          return breakdowns
            .map((breakdown): SingleDataPoint<{ primary: string }> => {
              const value = (() => {
                if (kind === 'current') return breakdown.current_count;
                if (isInflow) return breakdown.inflows_count;
                return breakdown.outflows_count;
              })();

              const share = (() => {
                if (kind === 'current') return breakdown.current_share;
                if (isInflow) return breakdown.inflows_share;
                return breakdown.outflows_share;
              })();

              return {
                dim: breakdown.short_name,
                value,
                share,
                primary: primaryLabel,
              };
            })
            .filter(
              (item) =>
                item.dim &&
                item.dim.toLowerCase() !== 'unknown' &&
                item.dim.toLowerCase() !== 'empty'
            );
        });

      const currentItems = buildItems('current');
      const flowItems = buildItems('flow');

      return {
        type: DataType.BREAKDOWN_SINGLE,
        data: [
          { dim: 'Current', breakdown: currentItems },
          { dim: isInflow ? 'Inflow' : 'Outflow', breakdown: flowItems },
        ],
      };
    }

    // Default behaviour (non-table charts) – pick the switcher-selected entity
    // (or fall back to the first) and apply top grouping.
    const entity = pickSelectedEntity(data?.entities, selectedEntity);

    const currentEntityMeasures = entity?.metrics?.breakdowns?.[
      secondaryDimension
    ]
      ?.map((breakdown): SingleDataPoint => {
        return {
          dim: breakdown.short_name,
          value: breakdown.current_count,
          share: breakdown.current_share,
        };
      })
      .filter(
        (item) =>
          item.dim &&
          item.dim.toLowerCase() !== 'unknown' &&
          item.dim.toLowerCase() !== 'empty'
      );

    const flowEntityMeasures = entity?.metrics?.breakdowns?.[secondaryDimension]
      ?.map((breakdown): SingleDataPoint => {
        return {
          dim: breakdown.short_name,
          value: isInflow ? breakdown.inflows_count : breakdown.outflows_count,
          share: isInflow ? breakdown.inflows_share : breakdown.outflows_share,
        };
      })
      .filter(
        (item) =>
          item.dim &&
          item.dim.toLowerCase() !== 'unknown' &&
          item.dim.toLowerCase() !== 'empty'
      );

    const shouldSort = ['role', 'geography', 'rics'].includes(
      secondaryDimension
    );
    const sortedCurrentMeasures = shouldSort
      ? [...(currentEntityMeasures ?? [])].sort(
          (a, b) => (b.share ?? 0) - (a.share ?? 0)
        )
      : currentEntityMeasures;
    const sortedFlowMeasures = shouldSort
      ? [...(flowEntityMeasures ?? [])].sort(
          (a, b) => (b.share ?? 0) - (a.share ?? 0)
        )
      : flowEntityMeasures;

    const topCurrentItems = sortedCurrentMeasures?.slice(0, 7);
    const topItemIds = topCurrentItems?.map((item) => item.dim);
    const otherCurrentItems = sortedCurrentMeasures?.slice(7);

    const topFlowItems = sortedFlowMeasures?.filter((item) =>
      topItemIds?.includes(item.dim)
    );
    const otherFlowItems = sortedFlowMeasures?.filter(
      (item) => !topItemIds?.includes(item.dim)
    );

    const otherCurrentTotal = otherCurrentItems?.reduce(
      (sum, item) => sum + (item.value ?? 0),
      0
    );
    const otherFlowTotal = otherFlowItems?.reduce(
      (sum, item) => sum + (item.value ?? 0),
      0
    );

    const allCurrentItems: SingleDataPoint[] = [
      ...(topCurrentItems ?? []),
      ...(otherCurrentTotal && otherCurrentTotal > 0
        ? [{ dim: 'Other', value: otherCurrentTotal }]
        : []),
    ];
    const allFlowItems: SingleDataPoint[] = [
      ...(topFlowItems ?? []),
      ...(otherFlowTotal && otherFlowTotal > 0
        ? [{ dim: 'Other', value: otherFlowTotal }]
        : []),
    ];

    return {
      type: DataType.BREAKDOWN_SINGLE,
      data: [
        { dim: 'Current', breakdown: allCurrentItems },
        { dim: isInflow ? 'Inflow' : 'Outflow', breakdown: allFlowItems },
      ],
    };
  }

  const flowMetricName: keyof NonNullable<
    TransitionV2DataQuery['transitionsV2']
  >['entities'][number]['metrics']['flows'] = (() => {
    switch (metric) {
      case 'transitions_inflow':
        return 'inflows';
      case 'transitions_outflow':
        return 'outflows';
    }
  })();
  const transitionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
    (entity) => {
      const metadata = entity.metadata;
      const dim = metadata?.short_name;

      const id =
        metadata?.id !== undefined && metadata?.id !== null
          ? `${metadata.type}:${metadata.id}`
          : undefined;

      const breakdown: SingleDataPoint[] = entity.metrics?.flows?.[
        flowMetricName
      ]
        ?.filter(
          (flow) =>
            flow.short_name &&
            flow.short_name.toLowerCase() !== 'empty' &&
            flow.short_name.toLowerCase() !== 'unknown'
        )
        ?.map((flow) => ({
          dim: flow.short_name,
          id:
            flow.id !== undefined && flow.id !== null
              ? `${metadata?.type}:${flow.id}`
              : undefined,
          value: flow.count,
          share: flow.share / 100,
        }));

      return { dim, id, breakdown };
    }
  );

  return { type: DataType.BREAKDOWN_SINGLE, data: transitionData };
};

const EXCLUDED_FLOW_DIMS: ReadonlySet<string> = new Set(['unknown', 'empty']);

function mapFlowToDataPoint(
  flow: {
    short_name?: string | null;
    id?: number | null;
    count?: number | null;
    share?: number | null;
  },
  entityType: string | null | undefined
): SingleDataPoint | null {
  if (
    !flow.short_name ||
    EXCLUDED_FLOW_DIMS.has(flow.short_name.toLowerCase())
  ) {
    return null;
  }
  return {
    dim: flow.short_name,
    id:
      flow.id !== undefined && flow.id !== null
        ? `${entityType}:${flow.id}`
        : undefined,
    value: flow.count ?? 0,
    share: (flow.share ?? 0) / 100,
  };
}

function convertInflowsAndOutflowsSankeyData({
  data,
}: {
  data: NonNullable<TransitionV2DataQuery['transitionsV2']>;
}): TransitionData {
  const transitionData: TransitionBreakdownData[] = data?.entities?.map(
    (entity) => {
      const metadata = entity.metadata;
      const dim = metadata?.short_name;
      const entityType = metadata?.type;

      const id =
        metadata?.id !== undefined && metadata?.id !== null
          ? `${entityType}:${metadata.id}`
          : undefined;

      const inflowBreakdown =
        entity.metrics?.flows?.inflows
          ?.map((flow) => mapFlowToDataPoint(flow, entityType))
          .filter((item): item is SingleDataPoint => item !== null) ?? [];

      const outflowBreakdown =
        entity.metrics?.flows?.outflows
          ?.map((flow) => mapFlowToDataPoint(flow, entityType))
          .filter((item): item is SingleDataPoint => item !== null) ?? [];

      return { dim, id, breakdown: [], inflowBreakdown, outflowBreakdown };
    }
  );

  return { type: DataType.BREAKDOWN_SINGLE, data: transitionData };
}

const SORTED_TRANSITION_SECONDARY_DIMENSIONS: ReadonlyArray<TransitionSecondaryDimensionValues> =
  ['role', 'geography', 'rics'];

type DimFlowEntry = {
  dim: string;
  inflowCount: number;
  outflowCount: number;
};

// Have to exclude these until MW updates the categories
const EXCLUDED_BREAKDOWN_DIMS: ReadonlySet<string> = new Set([
  'unknown',
  'empty',
  'other',
]);

function extractDimFlowEntries(
  entity: TransitionsEntity | undefined,
  secondaryDimension: TransitionSecondaryDimensionValues
): DimFlowEntry[] {
  const breakdowns = entity?.metrics?.breakdowns?.[secondaryDimension] ?? [];
  return breakdowns
    .filter(
      (breakdown) =>
        breakdown.short_name &&
        !EXCLUDED_BREAKDOWN_DIMS.has(breakdown.short_name.toLowerCase())
    )
    .map((breakdown) => ({
      dim: breakdown.short_name,
      inflowCount: breakdown.inflows_count,
      outflowCount: breakdown.outflows_count,
    }));
}

function convertInflowsAndOutflowsData({
  data,
  secondaryDimension,
  selectedEntity,
  limit,
}: {
  data: NonNullable<TransitionV2DataQuery['transitionsV2']>;
  secondaryDimension?: TransitionSecondaryDimensionValues;
  selectedEntity?: PrimaryEntity;
  limit?: number;
}): TransitionData {
  if (!secondaryDimension) {
    return { type: DataType.BREAKDOWN_SINGLE, data: [] };
  }

  const entity = pickSelectedEntity(data?.entities, selectedEntity);
  const entries = extractDimFlowEntries(entity, secondaryDimension);

  const shouldSort =
    SORTED_TRANSITION_SECONDARY_DIMENSIONS.includes(secondaryDimension);
  const sortedEntries = shouldSort
    ? [...entries].sort(
        (a, b) =>
          b.inflowCount + b.outflowCount - (a.inflowCount + a.outflowCount)
      )
    : entries;

  const limitCount = limit ?? 10;
  const topEntries = sortedEntries.slice(0, limitCount);

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: [
      {
        dim: 'Inflow',
        breakdown: topEntries.map((e) => ({
          dim: e.dim,
          value: e.inflowCount,
        })),
      },
      {
        dim: 'Outflow',
        breakdown: topEntries.map((e) => ({
          dim: e.dim,
          value: e.outflowCount,
        })),
      },
    ],
  };
}
